<template>
    <AppPopup :visible="visible" @close="$emit('close')" size="lg">
        <template #header>
            <div class="tw-flex tw-items-center tw-gap-3">
                <div class="tw-w-10 tw-h-10 tw-bg-primary-100 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
                    <icon icon="solar:buildings-2-bold-duotone" class="tw-text-primary-600 tw-text-xl" />
                </div>
                <div>
                    <h2 class="tw-text-xl tw-font-semibold tw-text-gray-900">{{ $t('common.edit-company') }}</h2>
                    <p class="tw-text-sm tw-text-gray-600">{{ $t('forms.company.edit-description', 'Update company information') }}</p>
                </div>
            </div>
        </template>

        <template #body>
            <form @submit.prevent="handleSubmit" class="tw-space-y-6">
                <!-- Company Name -->
                <FormField
                    v-model="form.name"
                    type="text"
                    :label="$t('fields.name')"
                    :placeholder="$t('forms.company.name-placeholder')"
                    left-icon="solar:buildings-2-bold-duotone"
                    required
                    :error="errors.name"
                />

                <!-- Primary Email -->
                <FormField
                    v-model="form.primary_email"
                    type="email"
                    :label="$t('forms.company.primary-email')"
                    :placeholder="$t('forms.company.primary-email-placeholder')"
                    left-icon="solar:letter-bold-duotone"
                    required
                    :error="errors.primary_email"
                />

                <!-- Website -->
                <FormField
                    v-model="form.website"
                    type="url"
                    :label="$t('forms.company.website')"
                    :placeholder="$t('forms.company.website-placeholder')"
                    left-icon="solar:earth-bold-duotone"
                    :error="errors.website"
                />

                <!-- Primary Phone -->
                <FormField
                    v-model="form.primary_phone"
                    type="tel"
                    :label="$t('forms.company.primary-phone')"
                    :placeholder="$t('forms.company.primary-phone-placeholder')"
                    left-icon="solar:phone-bold-duotone"
                    :error="errors.primary_phone"
                />

                <!-- Country -->
                <FormVueSelect
                    v-model="form.country_id"
                    :options="countriesOptions"
                    option-value="id"
                    option-label="name"
                    :label="$t('forms.company.country')"
                    :placeholder="$t('forms.company.select-country')"
                    left-icon="solar:map-point-bold-duotone"
                    :error="errors.country_id"
                    searchable
                    clearable
                />

                <!-- Address -->
                <FormField
                    v-model="form.address"
                    type="text"
                    :label="$t('fields.customer_address')"
                    :placeholder="$t('forms.company.address-placeholder')"
                    left-icon="solar:map-point-bold-duotone"
                    :error="errors.address"
                />

                <!-- Description -->
                <FormField
                    v-model="form.description"
                    type="textarea"
                    :label="$t('forms.company.description')"
                    :placeholder="$t('forms.company.description-placeholder')"
                    :error="errors.description"
                    rows="3"
                />
            </form>
        </template>

        <template #footer>
            <div class="tw-flex tw-items-center tw-justify-end tw-gap-3">
                <a-button 
                    type="button" 
                    intent="simple" 
                    @click="$emit('close')"
                    :disabled="isSubmitting"
                >
                    {{ $t('buttons.cancel') }}
                </a-button>
                <a-button 
                    type="submit" 
                    intent="primary" 
                    :loading="isSubmitting"
                    :disabled="isSubmitting"
                    @click="handleSubmit"
                >
                    {{ $t('buttons.update') }}
                </a-button>
            </div>
        </template>
    </AppPopup>
</template>

<script setup>
import { ref, reactive, onMounted, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import AppPopup from '@/components/AppPopup.vue';
import FormField from '@/components/form/FormField.vue';
import FormVueSelect from '@/components/form/FormVueSelect.vue';
import useAlert from '@/composables/useAlert';
import companyApi from '@/api/companies';
import { forms } from '@/api/app';

const { t } = useI18n();

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    company: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['close', 'updated']);

// Form data
const form = reactive({
    name: '',
    primary_email: '',
    website: '',
    primary_phone: '',
    country_id: '',
    address: '',
    description: ''
});

// Form state
const isSubmitting = ref(false);
const errors = reactive({});

// Options
const countriesOptions = ref([]);

// Watch for company changes and populate form
watch(() => props.company, (newCompany) => {
    if (newCompany) {
        form.name = newCompany.name || '';
        form.primary_email = newCompany.primary_email || '';
        form.website = newCompany.website || '';
        form.primary_phone = newCompany.primary_phone || '';
        form.country_id = newCompany.country_id || '';
        form.address = newCompany.address || '';
        form.description = newCompany.description || '';
    }
}, { immediate: true });

// Form validation
const validateForm = () => {
    errors.value = {};

    if (!form.name.trim()) {
        errors.name = t('errors.required', { field: t('fields.name') });
    }

    if (!form.primary_email.trim()) {
        errors.primary_email = t('errors.required', { field: t('forms.company.primary-email') });
    } else if (!isValidEmail(form.primary_email)) {
        errors.primary_email = t('errors.email');
    }

    if (form.website && !isValidUrl(form.website)) {
        errors.website = t('errors.url');
    }

    if (form.primary_phone && !isValidPhone(form.primary_phone)) {
        errors.primary_phone = t('errors.phone');
    }

    return Object.keys(errors).length === 0;
};

const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

const isValidPhone = (phone) => {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
};

// Form submission
const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    isSubmitting.value = true;

    try {
        const response = await companyApi.admin.update(props.company.id, form);
        
        useAlert(t('messages.company-updated', 'Company updated successfully'), 'success');
        emit('updated', response.data);
        
    } catch (error) {
        console.error('Error updating company:', error);
        
        if (error.response?.status === 422) {
            // Handle validation errors from server
            const serverErrors = error.response.data.errors;
            Object.keys(serverErrors).forEach(key => {
                errors[key] = serverErrors[key][0];
            });
        } else {
            useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        }
    } finally {
        isSubmitting.value = false;
    }
};

// Load form data
const loadFormData = async () => {
    try {
        const { data: countries } = await forms.countries();
        countriesOptions.value = countries;
    } catch (error) {
        console.error('Error loading form data:', error);
    }
};

onMounted(() => {
    loadFormData();
});
</script>

<style scoped>
/* Add any custom styles here */
</style> 